import React from 'react';
import { GridComponent, ColumnsDirective as GridColumnsDirective, ColumnDirective as GridColumnDirective } from '@syncfusion/ej2-react-grids';
import { CheckBoxComponent, ChangeEventArgs as CheckBoxChangeEventArgs } from '@syncfusion/ej2-react-buttons';
import { getMapClassMessage } from './utils/configurationHelpers';
import ClassificationTags from '../knowledgeLibrary/ClassificationTags';

declare var InovxLoader: any;

export interface ImportMappingSectionProps {
    configurationName: string;
    sortedFriendClasses: IFriendClass[];
    onAddFriendClass: () => void;
    onEditFriendClass: (name: string) => void;
    onDeleteFriendClass: (name: string) => void;
    assetSetTaxonomyDetail?: ILibraryTaxonomyDetail | null;
    autoPopulateAssetSets?: boolean;
    selectedAssetSetClassificationTags?: string[];
    hasAutoPopulateClassificationError?: boolean;
    onAutoPopulateAssetSetsChange: (enabled: boolean) => void;
    onAssetSetClassificationTagsChange: (selectedTags: string[]) => void;
}

const ImportMappingSection: React.FC<ImportMappingSectionProps> = ({
    configurationName,
    sortedFriendClasses,
    onAddFriendClass,
    onEditFriendClass,
    onDeleteFriendClass,
    assetSetTaxonomyDetail,
    autoPopulateAssetSets = false,
    selectedAssetSetClassificationTags = [],
    hasAutoPopulateClassificationError = false,
    onAutoPopulateAssetSetsChange,
    onAssetSetClassificationTagsChange
}) => {
    const [tableHeight, setTableHeight] = React.useState(window.innerHeight - 505);
    //make tableHeight responsive to window resize
    React.useEffect(() => {
        const handleResize = () => {
            const newTableHeight = window.innerHeight - 505;
            if (newTableHeight !== tableHeight) {
                // Force update to re-render the component with the new table height
                setTableHeight(newTableHeight);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [tableHeight]);
    
    return (
        <div className="configuration-form-tab-pane">
            <div className="row d-flex">
                <div className="col-md-12 p-2">
                    <div className="ctm-control-label">
                        <div className="d-flex a-ic" style={{ alignItems: 'center', width: '100%' }}>
                            {getMapClassMessage(configurationName || '')}
                        </div>
                        <button type="button" className="configuration-form-add-primary-btn plus-btn mt-1" onClick={onAddFriendClass} title={InovxLoader.t('facility:form.addAttributeTitle')}>
                            <span className="plus-btn-icon"><i className="fa fa-plus" /></span> <div className="configuration-form-panel-title">{InovxLoader.t('facility:form.addExternalClassName')}</div>
                        </button>
                    </div>
                </div>
            </div>
            <div className="configuration-import-mapping-lists overflow-visible">
                <div className="configuration-import-mapping-list mb-1 mt-10px">
                    <div className="configuration-grid responsive-grid">
                        <GridComponent
                            dataSource={sortedFriendClasses}
                            height={tableHeight}
                            allowPaging={false}
                            allowSorting={false}
                            allowSelection={false}
                            emptyRecordTemplate={InovxLoader.t('facility:message.noFriendClasses')}
                        >
                            <GridColumnsDirective>
                                <GridColumnDirective field="Name" headerText={InovxLoader.t('facility:form.vnfClassName')} width="220" textAlign="Left" />
                                <GridColumnDirective headerText="." width="80" textAlign="Center" template={(props: IFriendClass) => (
                                    <span>
                                        <i className="fa fa-pencil" style={{ cursor: 'pointer', fontSize: '1.4em', color: '#337ab7', marginRight: 16 }} title={InovxLoader.t('facility:preferences.editButton')} onClick={() => onEditFriendClass(props.Name)}></i>
                                        <i className="fa fa-trash" style={{ cursor: 'pointer', fontSize: '1.4em', color: '#337ab7' }} title={InovxLoader.t('facility:form.delete')} onClick={() => onDeleteFriendClass(props.Name)}></i>
                                    </span>
                                )} />
                            </GridColumnsDirective>
                        </GridComponent>
                    </div>
                </div>
                {sortedFriendClasses.length > 0 && (
                    <div className="configuration-import-mapping-list mt-10px">
                        <div className="configuration-import-asset-sets-panel" style={{height: `${tableHeight+35}px`}}>
                            <div className="configuration-form-panel-title-floating">{InovxLoader.t('facility:form.assetSets')}</div>
                            <div className="mt-1 ctm-checkbox">
                                <CheckBoxComponent
                                    label={InovxLoader.t('facility:form.autoPopulate')}
                                    checked={autoPopulateAssetSets}
                                    change={(args: CheckBoxChangeEventArgs) => onAutoPopulateAssetSetsChange(!!args.checked)}
                                />
                            </div>
                            
                                <div className="configuration-import-asset-sets-error form-error" role="alert">
                                    {hasAutoPopulateClassificationError && (<><i className="fa fa-exclamation-triangle" aria-hidden="true" />
                                    <span>{InovxLoader.t('facility:message.assetSetClassificationRequired')}</span>
                                    </>)}
                                </div>
                            
                            <div
                                className={`configuration-import-asset-sets-tree taxonomy-wrapper${hasAutoPopulateClassificationError ? ' has-error' : ''}`}
                                style={{height: tableHeight-45+'px', position: 'relative'}}
                            >
                                {autoPopulateAssetSets ? <>
                                <ClassificationTags
                                    selectedTags={selectedAssetSetClassificationTags}
                                    onSelectedTagsChange={onAssetSetClassificationTagsChange}
                                    taxonomyType={TaxonomyType.AssetSet}
                                    taxonomyClassificationDetailOverride={assetSetTaxonomyDetail}
                                    height={tableHeight-100}
                                />
                                </> : <div className="configuration-import-asset-sets-empty-state">{InovxLoader.t('facility:message.noClassificationSelected')}</div>}
                                
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImportMappingSection;
